import { useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, Pencil, Trash2, Shield, ShieldOff, Eye, EyeOff, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { API_URL } from '@/config/api';

const PasswordInput = ({ value, onChange, show, onToggle, placeholder, required, minLength }) => (
  <div className="relative">
    <Input
      type={show ? 'text' : 'password'}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      minLength={minLength}
      className="pr-10"
    />
    <button
      type="button"
      onClick={onToggle}
      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
      tabIndex={-1}
    >
      {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
    </button>
  </div>
);

const AdminUsers = () => {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.is_admin;
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    is_admin: true,
  });
  const [passwordForm, setPasswordForm] = useState({
    password: '',
    confirmPassword: '',
  });
  const [showPwdNew, setShowPwdNew] = useState(false);
  const [showPwdConfirm, setShowPwdConfirm] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    } else {
      setLoading(false);
    }
  }, [isAdmin]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('載入失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password && formData.password !== formData.confirmPassword) {
      toast.error('兩次輸入的密碼不一致');
      return;
    }

    try {
      if (editingUser) {
        const updateData = {};
        if (formData.username && formData.username !== editingUser.username) {
          updateData.username = formData.username;
        }
        if (formData.email && formData.email !== editingUser.email) {
          updateData.email = formData.email;
        }
        if (formData.is_admin !== editingUser.is_admin) {
          updateData.is_admin = formData.is_admin;
        }
        // Admin editing self can include password
        const isSelf = editingUser.username === currentUser?.username;
        if (formData.password && isSelf) {
          updateData.password = formData.password;
        }
        if (Object.keys(updateData).length === 0) {
          toast.info('沒有修改');
          setDialogOpen(false);
          return;
        }
        await axios.put(`${API_URL}/users/${editingUser.id}`, updateData);
        toast.success('更新成功');
      } else {
        if (!formData.password) {
          toast.error('請輸入密碼');
          return;
        }
        await axios.post(`${API_URL}/auth/register`, {
          username: formData.username,
          email: formData.email,
          password: formData.password,
          is_admin: formData.is_admin,
        });
        toast.success('新增成功');
      }
      setDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || '操作失敗');
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (!passwordForm.password) {
      toast.error('請輸入新密碼');
      return;
    }
    if (passwordForm.password !== passwordForm.confirmPassword) {
      toast.error('兩次輸入的密碼不一致');
      return;
    }
    try {
      await axios.put(`${API_URL}/users/${editingUser.id}`, {
        password: passwordForm.password,
      });
      toast.success('密碼已更新');
      setPasswordDialogOpen(false);
      setEditingUser(null);
      setPasswordForm({ password: '', confirmPassword: '' });
      setShowPwdNew(false);
      setShowPwdConfirm(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || '更新密碼失敗');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API_URL}/users/${deleteTarget.id}`);
      toast.success('刪除成功');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || '刪除失敗');
    } finally {
      setDeleteTarget(null);
    }
  };

  const openDialog = (user = null) => {
    setShowPassword(false);
    setShowConfirmPassword(false);
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username,
        email: user.email,
        password: '',
        confirmPassword: '',
        is_admin: user.is_admin,
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const openPasswordDialog = (user) => {
    setEditingUser(user);
    setPasswordForm({ password: '', confirmPassword: '' });
    setShowPwdNew(false);
    setShowPwdConfirm(false);
    setPasswordDialogOpen(true);
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({
      username: '',
      email: '',
      password: '',
      confirmPassword: '',
      is_admin: true,
    });
  };

  // Non-admin: only show change own password
  if (!isAdmin) {
    const selfUser = currentUser;
    return (
      <AdminLayout>
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold">帳號管理</h1>
            <p className="text-muted-foreground">修改你的密碼</p>
          </div>
          <div className="bg-white rounded-lg border p-6 max-w-md">
            <form onSubmit={async (e) => {
              e.preventDefault();
              if (!passwordForm.password) {
                toast.error('請輸入新密碼');
                return;
              }
              if (passwordForm.password !== passwordForm.confirmPassword) {
                toast.error('兩次輸入的密碼不一致');
                return;
              }
              try {
                await axios.put(`${API_URL}/users/${selfUser.id}`, {
                  password: passwordForm.password,
                });
                toast.success('密碼已更新');
                setPasswordForm({ password: '', confirmPassword: '' });
                setShowPwdNew(false);
                setShowPwdConfirm(false);
              } catch (error) {
                toast.error(error.response?.data?.detail || '更新密碼失敗');
              }
            }} className="space-y-4">
              <div>
                <Label>新密碼 *</Label>
                <PasswordInput
                  value={passwordForm.password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, password: e.target.value })}
                  show={showPwdNew}
                  onToggle={() => setShowPwdNew(!showPwdNew)}
                  placeholder="請輸入新密碼"
                  required
                  minLength={6}
                />
              </div>
              <div>
                <Label>確認新密碼 *</Label>
                <PasswordInput
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  show={showPwdConfirm}
                  onToggle={() => setShowPwdConfirm(!showPwdConfirm)}
                  placeholder="請再次輸入新密碼"
                  required
                  minLength={6}
                />
              </div>
              <Button type="submit">更新密碼</Button>
            </form>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // Admin view: full user management
  const isSelfEditing = editingUser?.username === currentUser?.username;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-wrap justify-between items-start gap-3">
          <div>
            <h1 className="text-3xl font-bold">帳號管理</h1>
            <p className="text-muted-foreground">管理可登入後台的帳號</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => openDialog()}>
                <Plus className="mr-2 h-4 w-4" /> 新增帳號
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingUser ? '編輯帳號' : '新增帳號'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>帳號名稱 *</Label>
                  <Input
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    required={!editingUser}
                    placeholder="登入用帳號名稱"
                  />
                </div>
                <div>
                  <Label>電郵 *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required={!editingUser}
                    placeholder="example@email.com"
                  />
                </div>
                {/* Password fields: show for new user OR when editing self */}
                {(!editingUser || isSelfEditing) && (
                  <>
                    <div>
                      <Label>{editingUser ? '新密碼（留空則不修改）' : '密碼 *'}</Label>
                      <PasswordInput
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        show={showPassword}
                        onToggle={() => setShowPassword(!showPassword)}
                        placeholder={editingUser ? '留空則保持不變' : '請輸入密碼'}
                        required={!editingUser}
                        minLength={6}
                      />
                    </div>
                    <div>
                      <Label>{editingUser ? '確認新密碼' : '確認密碼 *'}</Label>
                      <PasswordInput
                        value={formData.confirmPassword}
                        onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                        show={showConfirmPassword}
                        onToggle={() => setShowConfirmPassword(!showConfirmPassword)}
                        placeholder="請再次輸入密碼"
                        required={!editingUser}
                        minLength={6}
                      />
                    </div>
                  </>
                )}
                {editingUser && !isSelfEditing && (
                  <p className="text-sm text-muted-foreground bg-gray-50 p-3 rounded-lg">
                    管理員不能更改其他用戶的密碼。用戶需自行在「帳號管理」頁面修改密碼。
                  </p>
                )}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_admin"
                    checked={formData.is_admin}
                    onChange={(e) => setFormData({ ...formData, is_admin: e.target.checked })}
                  />
                  <Label htmlFor="is_admin">管理員權限</Label>
                </div>
                <div className="flex gap-2">
                  <Button type="submit">儲存</Button>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    取消
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Change own password dialog */}
        <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>修改我的密碼</DialogTitle>
            </DialogHeader>
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <Label>新密碼 *</Label>
                <PasswordInput
                  value={passwordForm.password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, password: e.target.value })}
                  show={showPwdNew}
                  onToggle={() => setShowPwdNew(!showPwdNew)}
                  placeholder="請輸入新密碼"
                  required
                  minLength={6}
                />
              </div>
              <div>
                <Label>確認新密碼 *</Label>
                <PasswordInput
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  show={showPwdConfirm}
                  onToggle={() => setShowPwdConfirm(!showPwdConfirm)}
                  placeholder="請再次輸入新密碼"
                  required
                  minLength={6}
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit">更新密碼</Button>
                <Button type="button" variant="outline" onClick={() => setPasswordDialogOpen(false)}>
                  取消
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {loading ? (
          <div className="text-center py-12">載入中...</div>
        ) : users.length > 0 ? (
          <div className="bg-white rounded-lg border overflow-x-auto">
            <table className="w-full min-w-[480px]">
              <thead className="border-b bg-gray-50">
                <tr>
                  <th className="text-left p-4">帳號</th>
                  <th className="text-left p-4 hidden md:table-cell">電郵</th>
                  <th className="text-left p-4">權限</th>
                  <th className="text-left p-4 hidden md:table-cell">建立時間</th>
                  <th className="text-right p-4">操作</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const isSelf = u.username === currentUser?.username;
                  return (
                    <tr key={u.id} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{u.username}</span>
                          {isSelf && (
                            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">你</span>
                          )}
                        </div>
                      </td>
                      <td className="p-4 hidden md:table-cell text-sm text-muted-foreground">{u.email}</td>
                      <td className="p-4">
                        {u.is_admin ? (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-green-100 text-green-800 rounded font-medium">
                            <Shield className="h-3 w-3" /> 管理員
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
                            <ShieldOff className="h-3 w-3" /> 一般
                          </span>
                        )}
                      </td>
                      <td className="p-4 hidden md:table-cell text-sm text-muted-foreground">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString('zh-HK') : '—'}
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex gap-2 justify-end">
                          <Button size="sm" variant="outline" onClick={() => openDialog(u)} title="編輯帳號">
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {isSelf && (
                            <Button size="sm" variant="outline" onClick={() => openPasswordDialog(u)} title="修改密碼">
                              <KeyRound className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="destructive"
                            disabled={isSelf}
                            onClick={() => setDeleteTarget({ id: u.id, username: u.username })}
                            title="刪除帳號"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            暫無帳號資料。
          </div>
        )}

      <AlertDialog open={!!deleteTarget} onOpenChange={o => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確定要刪除帳號「{deleteTarget?.username}」？</AlertDialogTitle>
            <AlertDialogDescription>此操作不可撤銷，帳號將永久刪除。</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">確定刪除</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default AdminUsers;
